import { prettyMilliseconds } from "@-/com.npmjs.pretty-ms";
import { AppState } from "@-/com.npmjs.react-native";
import { startOfNextLocalDay } from "@-/dev.improve.haniverse.lib.time.local-day";
import { useEffect, useState } from "react";

const MINUTE_MS = 60_000;

/**
 * How long until today's new-grapheme allowance renews, in whole minutes rounded up ("5h 12m",
 * "48m", never "0m"): the next local midnight, when the introduced-today count starts over (see
 * `useGraphemesIntroducedTodayQueryOptions`).
 */
export function timeUntilNextDailyNewGraphemesLabel(now: Date): string {
  const minutes = Math.max(
    1,
    Math.ceil((startOfNextLocalDay(now).getTime() - now.getTime()) / MINUTE_MS),
  );
  return prettyMilliseconds(minutes * MINUTE_MS, { unitCount: 2 });
}

/**
 * {@link timeUntilNextDailyNewGraphemesLabel} for now, re-rendering as each minute passes and when
 * the app returns to the foreground (a backgrounded app runs no timers).
 */
export function useTimeUntilNextDailyNewGraphemes(): string {
  const [label, setLabel] = useState(() => timeUntilNextDailyNewGraphemesLabel(new Date()));
  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const refresh = () => {
      const now = new Date();
      setLabel(timeUntilNextDailyNewGraphemesLabel(now));
      clearTimeout(timeout);
      // A beat after the label's next minute boundary, so the new label is the next minute's.
      const msUntilNextMinute =
        (startOfNextLocalDay(now).getTime() - now.getTime()) % MINUTE_MS || MINUTE_MS;
      timeout = setTimeout(refresh, msUntilNextMinute + 250);
    };
    refresh();
    const subscription = AppState.addEventListener("change", (state) => {
      if (state === "active") {
        refresh();
      }
    });
    return () => {
      clearTimeout(timeout);
      subscription.remove();
    };
  }, []);
  return label;
}
